import * as R from 'ramda'
import i18n from 'i18next'
import { observable, makeObservable, action } from "mobx"

import { getProductMap } from '../../utils/helpers'

class TemplateSearchStore {
    @observable createEmailTemplateModalVisible = false

    @observable deleteEmailTemplateModalVisible = false

    @observable testEmailModalVisible = false

    @observable eventFilter = []

    @observable sendingEmail = false

    @observable surveyFilter = []

    @observable respondentFilter = []

    @observable industryFilter = []

    @observable productFilter = []

    @observable publishStatusFilter = []

    @observable templateList = []

    @observable filteredList = []

    @observable invalidTestEmail = false

    @observable testEmailAddress = ''

    @observable isEditingSource = false

    @observable emailSender = ''

    @observable emailSubject = ''

    @observable tabName = 'email'

    @observable label = ''

    @observable hasRequiredError = false

    @observable hasRespondentRequiredError = false

    @observable emptySubjectError = false

    @observable emptyBodyError = false

    @observable checkboxOptions = {}

    @observable respondentCheckboxOptions = {}

    @observable isSaving = false

    @observable createPresentationModalVisible = false

    @observable editPresentationModalVisible = false

    @observable newTemplateName = ''

    @observable newTemplateSurveyType = null

    @observable newTemplateRespondentType = null

    @observable newTemplateEvent = null

    @observable editTemplateRow = null

    @observable templateSurveys = []

    editing = false

    templateId = ''

    respondentLabel = i18n.t('settings_respondent_access')

    divisionOptions = []

    portfolioOptions = []

    client = {}

    emailBody = ''

    eventTypeOptions = []

    surveyTypeOptions = []

    surveyTypeFilterOptions = []

    respondentTypeOptions = []

    respondentTypeFilterOptions = []

    productTypeOptions = []

    productTypeFilterOptions = []

    industryOptions = []

    industriesOptions = []

    industriesFilterOptions = []

    publishStatusOptions = []

    templateType = 'survey'

    constructor(store, type) {
        this.store = store
        this.templateType = type
        makeObservable(this)
    }

    init = async () => {
        await this.getDivisions()
        await this.getPortfolios()
        this.defineCheckboxFields()
        this.initRespondents()
        this.client = {
            id: R.path(['userData', 'clientId'], this.store),
            name: R.pathOr('', ['userData', 'clientName'], this.store),
            isMultiDivision: R.pathOr(true, ['userData', 'flags', 'isMultiDivision'], this.store),
        }
        this.label = this.client.isMultiDivision
            ? i18n.t('settings_division_access')
            : i18n.t('settings_portfolio_access')

        if (this.templateType === 'survey') {
            await this.populateCheckboxFields(this.initBody)
        }
        this.updating = false
    }

    @action
    initRespondents = async () => {
        const { store } = this
        const { clientId } = store.userData

        const prodMap = await getProductMap(store.http, clientId)

        this.industryOptions = []
        this.productTypeOptions = []
        this.surveyTypeOptions = []
        this.respondentTypeOptions = []

        Object.keys(prodMap).map((ind) => {
            this.industryOptions.push({
                label: prodMap[ind].label,
                id: ind,
                checked: false,
            })

            Object.keys(prodMap[ind].children).map((prod) => {
                this.productTypeOptions.push({
                    label: prodMap[ind].children[prod].label,
                    id: prod,
                    industryId: ind,
                    checked: false,
                })

                Object.keys(prodMap[ind].children[prod].children).map((st) => {
                    this.surveyTypeOptions.push({
                        label: prodMap[ind].children[prod].children[st].label,
                        id: st,
                        productId: prod,
                        industryId: ind,
                        checked: false,
                    })

                    Object.keys(prodMap[ind].children[prod].children[st].children).map((resp) => {
                        this.respondentTypeOptions.push({
                            label: prodMap[ind].children[prod].children[st].children[resp].label,
                            id: resp,
                            productId: prod,
                            industryId: ind,
                            surveyId: st,
                            checked: false,
                        })
                    })
                })
            })
        })
        this.defineRespondentCheckboxFields()
    }

    getDivisions = async () => {
        try {
            const res = await this.store.http.get('/division')
            this.divisionOptions = res.map((d) => ({
                label: d.name,
                id: d.divisionId,
                checked: false,
            }))
        } catch (error) {
            console.error('Error fetching divisions: ', error)
        }
    }

    getPortfolios = async () => {
        try {
            const res = await this.store.http.get('/portfolio')
            this.portfolioOptions = res.map((p) => ({
                label: p.name,
                id: p.portfolioId,
                divisionId: p.divisionId,
            }))
        } catch (error) {
            console.error('Error fetching portfolios: ', error)
        }
    }

    showCreateTemplateEmailModal = async (id = null) => {
        if (id) {
            this.editing = true
            this.templateId = id
            await this.getTemplate()
        } else {
            this.init()
        }
        this.createEmailTemplateModalVisible = true
    }

    showDeleteTemplateModal = (id) => {
        this.emailTemplateToDelete = id
        this.deleteEmailTemplateModalVisible = true
    }

    closeDeleteTemplateModal = () => {
        Reflect.deleteProperty(this, 'emailTemplateToDelete')
        this.deleteEmailTemplateModalVisible = false
    }

    deleteEmailTemplate = async () => {
        try {
            await this.store.http.delete(`/template/email/${this.emailTemplateToDelete}`)
            this.templateList = this.templateList.filter(({ id }) => id !== this.emailTemplateToDelete)
            this.store.addNotification({ body: i18n.t('templates.delete.email.success'), level: 'success' })
            this.filterTemplates()
        } catch (e) {
            console.error(e)
            this.store.handleError({ body: i18n.t('templates.delete.email.error'), level: 'error' })
        }
        this.closeDeleteTemplateModal()
    }

    deletePresentationTemplate = async () => {
        try {
            await this.store.http.delete(`/export/ppt/template/${this.emailTemplateToDelete}`)
            this.templateList = this.templateList.filter((t) => (t.uuid ?? t.id) !== this.emailTemplateToDelete)
            this.store.addNotification({ body: i18n.t('templates.delete.presentation.success'), level: 'success' })
            this.filterTemplates()
        } catch (e) {
            console.error(e)
            this.store.handleError({ body: i18n.t('templates.delete.presentation.error'), level: 'error' })
        }
        this.closeDeleteTemplateModal()
    }

    clonePresentationTemplate = async (id) => {
        try {
            await this.store.http.post(`/export/ppt/template/${id}/clone`, JSON.stringify({}))
            this.store.addNotification({ body: i18n.t('templates.clone.presentation.success'), level: 'success' })
            await this.getTemplates()
        } catch (e) {
            console.error(e)
            this.store.handleError({ body: i18n.t('templates.clone.presentation.error'), level: 'error' })
        }
    }

    getSurveysForTemplates = async () => {
        if (this.templateSurveys.length) return
        try {
            const res = await this.store.http.get('/survey')
            this.templateSurveys = res ?? []
        } catch (e) {
            console.error(e)
        }
    }

    resetPresentationTemplateForm = () => {
        this.newTemplateName = ''
        this.newTemplateSurveyType = null
        this.newTemplateRespondentType = null
        this.newTemplateEvent = null
    }

    openCreatePresentationModal = () => {
        this.resetPresentationTemplateForm()
        this.getSurveysForTemplates()
        this.createPresentationModalVisible = true
    }

    closeCreatePresentationModal = () => {
        this.createPresentationModalVisible = false
    }

    openEditPresentationModal = (row) => {
        this.editTemplateRow = row
        this.newTemplateEvent = null
        this.getSurveysForTemplates()
        this.editPresentationModalVisible = true
    }

    closeEditPresentationModal = () => {
        this.editPresentationModalVisible = false
        this.editTemplateRow = null
    }

    setNewTemplateName = (e) => {
        this.newTemplateName = e.value
    }

    setNewTemplateSurveyType = (value) => {
        this.newTemplateSurveyType = value
        this.newTemplateRespondentType = null
        this.newTemplateEvent = null
    }

    setNewTemplateRespondentType = (value) => {
        this.newTemplateRespondentType = value
        this.newTemplateEvent = null
    }

    setNewTemplateEvent = (value) => {
        this.newTemplateEvent = value
    }

    get templateSurveyTypeOptions() {
        const types = new Set()
        this.templateSurveys.forEach((s) => types.add(s.surveyType))
        return Array.from(types).map((value) => ({ value, label: this.getSurveyType(value) }))
    }

    templateRespondentTypeOptions = (surveyType) => {
        const types = new Set()
        this.templateSurveys
            .filter((s) => s.surveyType === surveyType)
            .forEach((s) => types.add(s.respondentType))
        return Array.from(types).map((value) => ({ value, label: this.getRespondentType(value) }))
    }

    templateEventOptions = (surveyType, respondentType) => {
        const events = new Map()
        this.templateSurveys
            .filter((s) => s.surveyType === surveyType && s.respondentType === respondentType)
            .forEach((s) => {
                if (!events.has(s.productId)) events.set(s.productId, s.productName)
            })
        return Array.from(events.entries()).map(([value, label]) => ({ value, label }))
    }

    resolveExampleSurvey = (surveyType, respondentType, productId) => {
        const matches = this.templateSurveys
            .filter((s) => s.surveyType === surveyType
                && s.respondentType === respondentType
                && s.productId === productId)
            .sort((a, b) => b.year - a.year)
        return matches[0]
    }

    createPresentationTemplate = async (history) => {
        const example = this.resolveExampleSurvey(
            this.newTemplateSurveyType,
            this.newTemplateRespondentType,
            this.newTemplateEvent,
        )
        if (!example) return
        try {
            const response = await this.store.http.post('/export/ppt/template', JSON.stringify({
                title: this.newTemplateName,
                surveyType: this.newTemplateSurveyType,
                respondentType: this.newTemplateRespondentType,
                productType: example.producttypecode,
            }))
            const newUuid = response?.uuid
            this.closeCreatePresentationModal()
            if (newUuid && history) {
                history.push(`/presentation-templates/${newUuid}`, { surveyId: example.surveyId })
            } else {
                this.store.handleError({ body: i18n.t('templates.create.presentation.error'), level: 'error' })
            }
        } catch (e) {
            console.error(e)
            this.store.handleError({ body: i18n.t('templates.create.presentation.error'), level: 'error' })
        }
    }

    openTemplateBuilderForEdit = (history) => {
        const row = this.editTemplateRow
        if (!row) return
        const example = this.resolveExampleSurvey(row.surveyType, row.respondentType, this.newTemplateEvent)
        if (!example) return
        this.closeEditPresentationModal()
        if (history) {
            history.push(`/presentation-templates/${row.uuid}`, { surveyId: example.surveyId })
        }
    }

    closeCreateTemplateEmailModal = () => {
        this.emailBody = ''
        this.emailSender = ''
        this.emailSubject = ''
        this.editing = false
        this.templateId = ''
        this.tabName = 'email'
        this.checkboxOptions = {}
        this.respondentCheckboxOptions = {}
        this.label = ''
        this.createEmailTemplateModalVisible = false
        this.resetErrors()
    }

    showTestEmailModal = () => {
        this.testEmailModalVisible = true
    }

    hideTestEmailModal = () => {
        this.testEmailModalVisible = false
    }

    changeTestEmailAddress = (e) => {
        this.invalidTestEmail = false
        this.testEmailAddress = e.value

        if (!this.testEmailAddress || !e.value.match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/g)) {
            this.invalidTestEmail = true
        }
    }

    sendTestEmail = async () => {
        const { store } = this

        if (!this.testEmailAddress || this.testEmailAddress.split('@').length !== 2) {
            return
        }

        try {
            this.sendingEmail = true
            await store.http.post(`/sendEmail`, JSON.stringify({
                subject: this.emailSubject,
                body: this.emailBody,
                to: [this.testEmailAddress],
            }))
            store.addNotification({ body: i18n.t('toasts.testEmail.success'), level: 'success' })
        } catch (e) {
            console.error(e)
            store.handleError({ body: i18n.t('toasts.testEmail.error'), level: 'error' })
        } finally {
            this.sendingEmail = false
            this.hideTestEmailModal()
            this.invalidTestEmail = false
            this.testEmailAddress = ''
        }
    }

    getTemplate = async () => {
        const resp = await this.store.http.get(`/template/email/${this.templateId}`)
        await this.getDivisions()
        await this.getPortfolios()
        this.emailBody = resp.emailTemplate.body
        this.emailSender = resp.emailTemplate.sender
        this.emailSubject = resp.emailTemplate.subject
        this.client = {
            id: R.path(['userData', 'clientId'], this.store),
            name: R.pathOr('', ['userData', 'clientName'], this.store),
            isMultiDivision: R.pathOr(true, ['userData', 'flags', 'isMultiDivision'], this.store),
        }
        this.label = this.client.isMultiDivision
        ? i18n.t('settings_division_access')
        : i18n.t('settings_portfolio_access')
        await this.initRespondents()
        this.defineCheckboxFields()
        this.populateEmailTemplateAccessFields(resp.userAccess)
        this.populateRespondentCheckboxFields(resp.respondent)
    }

    resetErrors = () => {
        this.emptyBodyError = false
        this.emptySubjectError = false
        this.hasRequiredError = false
        this.hasRespondentRequiredError = false
    }

    saveTemplate = async () => {
        this.isSaving = true
        try {
            this.resetErrors()
            const respondent = []
            const userAccess = []
            Object.values(this.checkboxOptions).forEach((ua) => {
                if (ua.checked) {
                    userAccess.push({
                        oid: 4,
                        rid: ua.id
                    })
                }

                ua.portfolios.forEach((p) => {
                    if (p.checked) {
                        userAccess.push({
                            oid: 5,
                            rid: p.id
                        })
                    }
                })
            })
            Object.values(this.respondentCheckboxOptions).forEach((ind) => {
                ind.productTypes.forEach((pt) => {
                    pt.surveyTypes.forEach((st) => {
                        st.respondentTypes.forEach((rt) => {
                            if (rt.checked) {
                                respondent.push({
                                    respondentType: rt.id,
                                    surveyType: rt.surveyId,
                                    productType: rt.productId,
                                    industry: rt.industryId,
                                })
                            }
                        })
                    })
                })
            })

            if (!this.emailSubject.length) {
                this.emptySubjectError = true
            }

            if (!this.emailBody.length) {
                this.emptyBodyError = true
            }

            if (!userAccess.length) {
                this.hasRequiredError = true
            }

            if (!respondent.length) {
                this.hasRespondentRequiredError = true
            }

            if (this.emptySubjectError || this.emptyBodyError || this.hasRequiredError || this.hasRespondentRequiredError) {
                this.isSaving = false
                return
            }
            const response = await this.store.http[this.editing ? 'put' : 'post'](this.editing ? `/template/email/${this.templateId}` : '/template/email', JSON.stringify({
                emailTemplate: {
                    body: this.emailBody,
                    subject: this.emailSubject,
                    sender: this.emailSender,
                },
                userAccess,
                respondent,
            }))

            this.store.addNotification({
                body: `Email template ${this.editing ? 'updated' : 'created'} successfully`,
                level: 'success',
            })

            const { emailTemplate: { id } } = response
            this.editing = true
            this.templateId = id

            await this.getTemplates()
        } catch (e) {
            console.log(e)
            this.store.handleError({ body: `Failed to ${this.editing ? 'update' : 'create'} template`, level: 'error' })
        }
        this.isSaving = false
    }

    changeEmailSubject = (e) => {
        this.emailSubject = e.value
    }

    changeEmailBody = (value) => {
        this.emailBody = value
    }

    changeEmailSender = (e) => {
        this.emailSender = e.value
    }

    editorSourceToggle = (isSourceEditing) => {
        this.isEditingSource = isSourceEditing
    }

    getEventType = (type) => {
        switch(type) {
            case 'B2B':
                return 'B2B Events'
            case 'B2C':
                return 'B2C Events'
            case 'DENT':
                return 'Dentistry'
            case 'EXPO':
                return 'EXPO2020'
            case 'PUCO':
                return 'Consumer'
            case 'PUTR':
                return 'Trade'
            case 'VAVA':
                return 'Visitor Attraction'
            case 'VI':
                return 'Virtual Event'
            case 'TR':
                return i18n.t('model.services.dashboardService.generateComment.producttype.TR')
            case 'CO':
                return i18n.t('model.services.dashboardService.generateComment.producttype.CO')
            case 'CF':
                return i18n.t('dashboard.producttype.EV.CF')
            case 'VI':
                return i18n.t('dashboard.producttype.EV.VI')
            case 'CE3P':
                return i18n.t('dashboard.producttype.CE.CE3P')
            case 'CECE':
                return i18n.t('dashboard.producttype.CE.CECE')
            case 'CEEE':
                return i18n.t('dashboard.producttype.CE.CEEE')
            default:
                return ''
        }
    }

    getSurveyType = (type) => {
        switch(type) {
            case 'PE':
                return i18n.t('surveyType_Benchmarking')
            case 'GE':
                return i18n.t('surveyType_General')
            case 'EM':
                return i18n.t('navmenu.maxbi')
            case 'CA':
                return 'Tracker'
            case 'P365':
                return '365 platform'
            case 'PC':
                return 'Product Process'
            case 'R':
                return 'Registration'
            case 'SP':
                return 'Sales Process'
            case 'VE':
                return 'Virtual event'
            case 'W':
                return 'Website'
            case 'WV':
                return 'Webinar / Virtual Event'
            default:
                return ''
        }
    }

    getRespondentType = (type) => {
        switch (type) {
            case 'EX':
                return i18n.t('model.services.dashboardService.generateComment.respondenttype.EX')
            case 'NN':
                return i18n.t('model.services.companyTableService.companyTable.filters.benchmarks.none')
            case 'PR':
                return i18n.t('refcodes_PRNA')
            case 'VI':
                return i18n.t('model.services.dashboardService.generateComment.respondenttype.VI')
            case 'RD':
                return i18n.t('templates.reader')
            case 'CEVI':
                return i18n.t('respondentType.CE.CEVI')
            case 'CEOT':
                return i18n.t('other')
            case 'CEST':
                return 'Staff'
            case 'CEVI':
                return i18n.t('model.services.dashboardService.generateComment.respondenttype.VI')
            case 'DEVI':
                return 'Patient'
            case 'EA':
                return 'Exhibit / Activation'
            case 'EE':
                return 'External Event'
            case 'EXOT':
                return i18n.t('other')
            case 'IE':
                return 'Internal Event'
            case 'MYST':
                return 'Mystery Shopping'
            case 'PUAD':
                return 'Advertiser'
            case 'PURE':
                return 'Reader'
            case 'VAVI':
                return i18n.t('model.services.dashboardService.generateComment.respondenttype.VI')
            case 'VIPO':
                return i18n.t('model.services.dashboardService.generateComment.respondenttype.VI')
            default:
                return ''
        }
    }

    getIndustry = (type) => {
        switch (type) {
            case 'CE':
                return 'Corporate Events'
            case 'DENT':
                return 'Dentistry'
            case 'EV':
                return 'Events'
            case 'EXPO':
                return 'EXPO2020'
            case 'PU':
                return 'Publications'
            case 'VA':
                return 'Visitor Attractions'
        }
    }

    setTabs = () => {
        return [
            {
                label: i18n.t('email'),
                name: 'email',
                reachable: true,
            },
            {
                label: i18n.t('portfolio'),
                name: 'portfolios',
                reachable: true,
            },
            {
                label: i18n.t('tab_product_types'),
                name: 'eventtypes',
                reachable: true,
            },
        ]
    }

    @action
    toggleTab = (tab) => {
        if (this.tabName !== tab) this.tabName = tab
    }

    @action
    toggleIndustry = ({ id }, val) => {
        this.respondentCheckboxOptions[id].checked = val

        this.respondentCheckboxOptions[id].productTypes.map((p) => {
            p.checked = val
            p.surveyTypes.map((st) => {
                st.checked = val
                st.respondentTypes.map((r) => {
                    r.checked = val
                })
            })
        })
    }

    @action
    toggleProduct = (field, val) => {
        field.checked = val

        field.surveyTypes.map((st) => {
            st.checked = val
            st.respondentTypes.map((r) => {
                r.checked = val
            })
        })
    }

    @action
    toggleSurvey = (field, val) => {
        field.checked = val

        field.respondentTypes.map((r) => {
            r.checked = val
        })
    }

    @action
    toggleRespondent = (field, val) => {
        field.checked = val
    }

    @action
    toggleDivision = (rid, val) => {
        Object.entries(this.checkboxOptions).forEach(([_, divisionData]) => {
            if (divisionData.id === rid) {
                divisionData.checked = val
                divisionData.portfolios.forEach((p) => { p.checked = val })
            }
        })
    }

    @action
    togglePortfolio = (rid, val) => {
        Object.entries(this.checkboxOptions).forEach(([_, divisionData]) => {
            const portfolio = divisionData.portfolios.find((p) => p.id === rid)
            if (portfolio) portfolio.checked = val
            const areAllSelected = divisionData.portfolios.length > 0 && divisionData.portfolios.every((p) => p.checked)
            this.checkboxOptions[divisionData.id].checked = areAllSelected
        })
    }

    @action
    toggleAll = (clientId, val) => {
        if (this.client.id !== clientId) return
        Object.entries(this.checkboxOptions).forEach(([_, divisionData]) => {
            divisionData.checked = val
            divisionData.portfolios.forEach((p) => { p.checked = val })
        })
    }

    defineCheckboxFields = () => {
        const { divisionOptions, portfolioOptions } = this
        const result = {}

        const portfoliosByDivision = new Map()

        portfolioOptions.forEach((p, _i) => {
            const { divisionId } = p
            if (divisionId) {
                if (!portfoliosByDivision.has(divisionId)) {
                    portfoliosByDivision.set(divisionId, [])
                }
                const portfolios = portfoliosByDivision.get(divisionId)
                portfolios.push({
                    name: p.label,
                    id: p.id,
                    checked: false,
                    divisionId: p.divisionId,
                })
                portfoliosByDivision.set(divisionId, portfolios)
            }
        })

        divisionOptions.forEach((d) => {
            const divisionPortfolios = portfoliosByDivision.get(d.id) || []
            result[d.id] = {
                name: d.label,
                id: d.id,
                checked: false,
                portfolios: divisionPortfolios,
            }
        })
        this.checkboxOptions = result
    }

    defineRespondentCheckboxFields = () => {
        const { industryOptions, productTypeOptions, surveyTypeOptions, respondentTypeOptions } = this

        const result = {}

        const survType = surveyTypeOptions.map((st) => {
            return {
                ...st,
                respondentTypes: respondentTypeOptions.filter((resp) => resp.surveyId === st.id && resp.productId === st.productId && resp.industryId === st.industryId)

            }
        })

        const prodType = productTypeOptions.map((prod) => {
            return {
                ...prod,
                surveyTypes: survType.filter((st) => st.productId === prod.id)
            }
        })

        const industries = industryOptions.map((ind) => {
            return {
                ...ind,
                productTypes: prodType.filter((prod) => prod.industryId === ind.id)
            }
        })

        industries.forEach((ind, _i) => {
            result[ind.id] = {
                ...ind
            }
        })

        this.respondentCheckboxOptions = result
    }

    populateCheckboxFields = async (initBody) => {
        try {
            const accessData = initBody || await this.store.http.get(`/template/${this.store.survey.id}/access`)
            accessData.map((ad) => {
                if (ad.oid === 3) { // client oid
                    this.toggleAll(ad.rid, true)
                } else if (ad.oid === 4) { // division oid
                    this.toggleDivision(ad.rid, true)
                } else if (ad.oid === 5) { // portfolio oid
                    this.togglePortfolio(ad.rid, true)
                }
            })
        } catch (error) {
            console.error(error)
        }
    }

    populateEmailTemplateAccessFields = (templateData) => {
        templateData.map((td) => {
            if (td.oid === 3) { // client oid
                this.toggleAll(td.rid, true)
            } else if (td.oid === 4) { // division oid
                this.toggleDivision(td.rid, true)
            } else if (td.oid === 5) { // portfolio oid
                this.togglePortfolio(td.rid, true)
            }
        })
    }

    populateRespondentCheckboxFields = (templateData) => {
        templateData.forEach((td) => {
            this.respondentCheckboxOptions[td.industry].checked = true

            this.respondentCheckboxOptions[td.industry].productTypes.map((pt) => {
                if (pt.id === td.productType) {
                    pt.checked = true
                    pt.surveyTypes.map((st) => {
                        if (st.id === td.surveyType) {
                            st.checked = true
                            st.respondentTypes.map((r) => {
                                if (r.id === td.respondentType) {
                                    r.checked = true
                                }
                            })
                        }
                    })
                }
            })
        })
    }

    toggleCheckbox = (obj, e, oid) => {
        const valueIdStr = e.target.getAttribute('valueid')
        const optionId = Number(valueIdStr)
        if (oid === 3) {
            this.toggleAll(this.client.id, obj.value)
        } else if (oid === 4) {
            this.toggleDivision(optionId, obj.value)
        } else if (oid === 5) {
            this.togglePortfolio(optionId, obj.value)
        }
    }

    toggleRespondentsCheckbox = (obj, e, field, oid) => {
        if (oid === 2) {
            this.toggleIndustry(field, obj.value)
        } else if (oid === 6) {
            this.toggleProduct(field, obj.value)
        } else if (oid === 7) {
            this.toggleSurvey(field, obj.value)
        } else if (oid === 12) {
            this.toggleRespondent(field, obj.value)
        }
    }

    filterTemplates = () => {
        let filtered = this.templateList
        if (this.respondentFilter.length) {
            filtered = filtered.filter((template) => {
                const respondentType = template.respondentType
                if (Array.isArray(respondentType)) {
                    return this.respondentFilter.some((item) => template.respondentType.includes(item))
                } else {
                    return this.respondentFilter.includes(template.respondentType)
                }
            })
        }

        if (this.surveyFilter.length) {
            filtered = filtered.filter((template) => {
                const surveyType = template.surveyType
                if (Array.isArray(surveyType)) {
                    return this.surveyFilter.some((item) => template.surveyType.includes(item))
                } else {
                    return this.surveyFilter.includes(template.surveyType)
                }
            })
        }

        if (this.productFilter.length) {
            filtered = filtered.filter((template) => {
                const productType = template.productType
                if (Array.isArray(productType)) {
                    return this.productFilter.some((item) => template.productType.includes(item))
                } else {
                    return this.productFilter.includes(template.productType)
                }
            })
        }

        if (this.industryFilter.length) {
            filtered = filtered.filter((template) => {
                const { industry } = template
                if (Array.isArray(industry)) {
                    return this.industryFilter.some((item) => template.industry.includes(item))
                }

                return this.industryFilter.includes(template.industry)
            })
        }

        if (R.pathOr('', ['searchModule', 'searchStr'], this).length) {
            const searchFieldByType = { survey: 'surveyName', presentation: 'name', email: 'subject' }
            const searchField = searchFieldByType[this.templateType] ?? 'subject'
            filtered = filtered.filter((template) => (
                R.pathOr('', [searchField], template).includes(this.searchModule.searchStr)
            ))
        }

        if (this.publishStatusFilter.length) {
            filtered = filtered.filter((template) => {
                if (this.publishStatusFilter.length === 2) return template
                return this.publishStatusFilter.includes(template.published)
            })
        }

        this.filteredList = filtered
    }

    getTemplates = async () => {
        try {
            const endpointByType = {
                survey: '/template',
                presentation: '/export/ppt/templates',
                email: '/template/email',
            }
            const response = await this.store.http.get(endpointByType[this.templateType] ?? '/template/email')

            this.templateList = response

            let availableSurveyTypes = []
            let availableRespondentTypes = []
            let availableIndustries = []
            let availableProductTypes = []
            let availablePublishStatuses = []

            response.forEach((row) => {
                if (typeof row.productType === 'object') {
                    row.productType.map((pt) => {
                        availableProductTypes.push(pt)
                    })
                } else {
                    availableProductTypes.push(row.productType)
                }

                if (typeof row.surveyType === 'object') {
                    row.surveyType.map((st) => {
                        availableSurveyTypes.push(st)
                    })
                } else {
                    availableSurveyTypes.push(row.surveyType)
                }

                if (typeof row.respondentType === 'object') {
                    row.respondentType.map((rt) => {
                        availableRespondentTypes.push(rt)
                    })
                } else {
                    availableRespondentTypes.push(row.respondentType)
                }

                if (row.industry) {
                    if (Array.isArray(row.industry)) {
                        availableIndustries.push(...row.industry)
                    } else {
                        availableIndustries.push(row.industry)
                    }
                }
                if ('published' in row) {
                    availablePublishStatuses.push(!!row.published)
                }
            })
            availableProductTypes = Array.from(new Set(availableProductTypes))
            availableSurveyTypes = Array.from(new Set(availableSurveyTypes))
            availableRespondentTypes = Array.from(new Set(availableRespondentTypes))
            availableIndustries = Array.from(new Set(availableIndustries))
            availablePublishStatuses = Array.from(new Set(availablePublishStatuses))

            this.productFilter = availableProductTypes
            this.surveyFilter = availableSurveyTypes
            this.respondentFilter = availableRespondentTypes
            this.industryFilter = availableIndustries
            this.publishStatusFilter = availablePublishStatuses

            this.surveyTypeFilterOptions = availableSurveyTypes.map((value) => {
                return {
                    label: this.getSurveyType(value),
                    value,
                }
            })

            this.respondentTypeFilterOptions = availableRespondentTypes.map((value) => {
                return {
                    label: this.getRespondentType(value),
                    value,
                }
            })

            this.industriesFilterOptions = availableIndustries.map((value) => {
                return {
                    label: this.getIndustry(value),
                    value,
                }
            })

            this.productTypeFilterOptions = availableProductTypes.map((value) => {
                return {
                    label: this.getEventType(value),
                    value,
                }
            })

            this.publishStatusOptions = availablePublishStatuses.map((value) => {
                return {
                    label: value ? i18n.t('templates.status.published') : i18n.t('templates.status.unpublished'),
                    value,
                }
            })

            this.filterTemplates()
        } catch (e) {
            console.log(e)
        }
    }

    changeProductFilter = (e) => {
        if (this.productFilter.includes(e.name)) {
            this.productFilter = this.productFilter.filter((val) => val !== e.name)
        } else {
            this.productFilter.push(e.name)
        }

        this.filterTemplates()
    }

    changeSurveyFilter = (e) => {
        if (this.surveyFilter.includes(e.name)) {
            this.surveyFilter = this.surveyFilter.filter((val) => val !== e.name)
        } else {
            this.surveyFilter.push(e.name)
        }

        this.filterTemplates()
    }

    changeRespondentFilter = (e) => {
        if (this.respondentFilter.includes(e.name)) {
            this.respondentFilter = this.respondentFilter.filter((val) => val !== e.name)
        } else {
            this.respondentFilter.push(e.name)
        }

        this.filterTemplates()
    }

    changeIndustryFilter = (e) => {
        if (this.industryFilter.includes(e.name)) {
            this.industryFilter = this.industryFilter.filter((val) => val !== e.name)
        } else {
            this.industryFilter.push(e.name)
        }

        this.filterTemplates()
    }

    changePublishedStatusFilter = (e) => {
        const value = e.name === i18n.t('templates.status.published')
        let newStatusFilter = this.publishStatusFilter

        if (e.value) {
            if (!newStatusFilter.includes(value)) {
                newStatusFilter = [...newStatusFilter, value]
            }
        } else {
            newStatusFilter = newStatusFilter.filter((val) => val !== value)
        }
        this.publishStatusFilter = newStatusFilter

        this.filterTemplates()
    }

    resetFilter = () => {
        this.respondentFilter = []
        this.eventFilter = []
        this.surveyFilter = []

        this.filterTemplates()
    }

    search = () => {
        this.filterTemplates()
    }
}

export default TemplateSearchStore
